import Markdown from "react-markdown";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import "./markdown.scss";

import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import java from "react-syntax-highlighter/dist/esm/languages/hljs/java";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import scss from "react-syntax-highlighter/dist/esm/languages/hljs/scss";
import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash";
import markdown from "react-syntax-highlighter/dist/esm/languages/hljs/markdown";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import { useState } from "react";

SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("scss", scss);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("markdown", markdown);
SyntaxHighlighter.registerLanguage("json", json);

const MarkdownContainer = ({ children }) => {
  const [copied, setCopied] = useState(false);

  return (
    <Markdown
      // GFM adds tables, strikethrough, autolinks and task lists. Without it
      // react-markdown is CommonMark-only and a pipe table renders as a
      // paragraph full of literal "|" characters.
      remarkPlugins={[remarkGfm]}
      // Order matters: rehype-raw must parse raw HTML strings into hast first,
      // then rehype-sanitize prunes it. This content can come from an arbitrary
      // third-party MCP server (tool_result) and is shown to anonymous visitors,
      // so it must never be rendered unsanitized.
      //
      // defaultSchema is used as-is — verified to already allow the table tags,
      // the `align` property that carries GFM column alignment (react-markdown
      // later renders it as an inline text-align style) and /^language-./
      // classNames on <code> (needed by the SyntaxHighlighter branch below),
      // while stripping <script> and event handlers such as onerror.
      rehypePlugins={[rehypeRaw, [rehypeSanitize, defaultSchema]]}
      components={{
        code(props) {
          const { children, className, node, ...rest } = props;

          const match = /language-(\w+)/.exec(className || "");
          const code = String(children).replace(/\n$/, "");
          const copyCode = async () => {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
          };

          return match ? (
            <div style={{ position: "relative" }}>
              <button
                onClick={copyCode}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  zIndex: 1,
                  backgroundColor: copied ? "#4CAF50" : "#2196F3",
                  color: "white",
                  border: "none",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
              <SyntaxHighlighter
                {...rest}
                children={code}
                style={atomOneDark}
                language={match[1]}
                PreTag="div"
                className="code_container"
              />
            </div>
          ) : (
            <code {...rest} className={className}>
              {children}
            </code>
          );
        },
        a(props) {
          return (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          );
        },
        // Only tables get a wrapper — deliberately not a wrapper around the whole
        // markdown output, so every other consumer's DOM (and the CSS that
        // targets markdown nodes as direct children, e.g.
        // ExampleMessageFeed.scss's `.message_item_content > *`) is unchanged.
        // The wrapper provides the horizontal scroll a wide table needs inside a
        // narrow chat bubble or the ~400px embeddable widget.
        table(props) {
          const { node, ...rest } = props;
          return (
            <div className="markdown_table_wrap">
              <table {...rest} />
            </div>
          );
        },
      }}
    >
      {children}
    </Markdown>
  );
};

export default MarkdownContainer;
